import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import {
  EMAIL_TEMPLATE_NAMES,
  ISO_639_1_LANGUAGE_CODES,
  mapEmailTemplateResponseToDto,
  mapJurisdictionEmailTemplatesResponseToDto,
  mapPlaceholdersResponseToDto,
} from '../dto/system-email-templates.dto.js';
import {
  buildEmailTemplatesTableModel,
  normalizeTemplateHtmlForSave,
  validateEmailTemplateForm,
} from '../model/system-email-templates.model.js';
import { mapJurisdictionsResponseToDto } from '../../system-kyc/dto/system-kyc.dto.js';

const DEFAULT_LANGUAGE = 'en';

function toLowercaseLanguage(value, fallback = DEFAULT_LANGUAGE) {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized || fallback;
}

export function useSystemEmailTemplatesList() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const queryJurisdictionId = String(searchParams.get('jurisdictionId') || '').trim();
  const queryLanguage = toLowercaseLanguage(searchParams.get('lang'), DEFAULT_LANGUAGE);

  const [rows, setRows] = useState([]);
  const [jurisdictions, setJurisdictions] = useState([]);
  const [selectedJurisdictionId, setSelectedJurisdictionId] = useState(queryJurisdictionId);
  const [isJurisdictionsLoading, setIsJurisdictionsLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [detailsTargetId, setDetailsTargetId] = useState('');
  const [editingTargetId, setEditingTargetId] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState({ name: EMAIL_TEMPLATE_NAMES[0], language: DEFAULT_LANGUAGE, subject: '', html: '' });
  const [formErrors, setFormErrors] = useState({});
  const [placeholderOptions, setPlaceholderOptions] = useState([]);

  const languageOptions = useMemo(() => {
    const usedLanguages = rows
      .map((row) => toLowercaseLanguage(row.language, ''))
      .filter(Boolean);
    return Array.from(new Set([...ISO_639_1_LANGUAGE_CODES, ...usedLanguages]));
  }, [rows]);

  const wrapLink = useMemo(() => {
    const params = new URLSearchParams();
    if (selectedJurisdictionId) {
      params.set('jurisdictionId', selectedJurisdictionId);
    }
    const nextLanguage = queryLanguage;
    if (nextLanguage) {
      params.set('lang', nextLanguage);
    }

    const query = params.toString();
    return `/system/user-settings/email-templates/header-footer${query ? `?${query}` : ''}`;
  }, [queryLanguage, selectedJurisdictionId]);

  const detailsRow = useMemo(
    () => rows.find((row) => row.id === detailsTargetId) || null,
    [rows, detailsTargetId],
  );

  useEffect(() => {
    setSelectedJurisdictionId(queryJurisdictionId);
  }, [queryJurisdictionId]);

  const updateJurisdictionQuery = useCallback((jurisdictionId) => {
    const nextParams = new URLSearchParams(searchParams);
    const nextValue = String(jurisdictionId || '').trim();

    if (nextValue) {
      nextParams.set('jurisdictionId', nextValue);
    } else {
      nextParams.delete('jurisdictionId');
    }

    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const loadJurisdictions = useCallback(async () => {
    setIsJurisdictionsLoading(true);

    try {
      const response = await adminApi.getJurisdictions(token);
      const list = mapJurisdictionsResponseToDto(response);
      setJurisdictions(list);

      if (list.length === 0) {
        setSelectedJurisdictionId('');
        updateJurisdictionQuery('');
        return;
      }

      const hasSelected = list.some((item) => item.id === queryJurisdictionId);
      if (hasSelected) {
        return;
      }

      const fallbackId = list[0].id;
      setSelectedJurisdictionId(fallbackId);
      updateJurisdictionQuery(fallbackId);
    } catch (requestError) {
      if (requestError?.status === 404) {
        setJurisdictions([]);
        setSelectedJurisdictionId('');
        updateJurisdictionQuery('');
      } else {
        setJurisdictions([]);
        setSelectedJurisdictionId('');
        setError(requestError.message || 'Failed to load jurisdictions');
      }
    } finally {
      setIsJurisdictionsLoading(false);
    }
  }, [queryJurisdictionId, token, updateJurisdictionQuery]);

  const loadTemplateDetails = useCallback(async (jurisdictionId, templateId, fallback = {}) => {
    const response = await adminApi.requestAdmin({
      token,
      method: 'GET',
      path: `/admin/system/user-settings/jurisdictions/${encodeURIComponent(jurisdictionId)}/email-templates/${encodeURIComponent(templateId)}`,
    });

    return mapEmailTemplateResponseToDto(response, fallback.name, fallback.language);
  }, [token]);

  const loadTemplates = useCallback(async () => {
    if (isJurisdictionsLoading) {
      return;
    }

    const isKnownJurisdiction = jurisdictions.some((item) => item.id === selectedJurisdictionId);
    if (!selectedJurisdictionId || !isKnownJurisdiction) {
      setRows([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const listResponse = await adminApi.requestAdmin({
        token,
        method: 'GET',
        path: `/admin/system/user-settings/jurisdictions/${encodeURIComponent(selectedJurisdictionId)}/email-templates`,
      });

      const listRows = mapJurisdictionEmailTemplatesResponseToDto(listResponse);
      if (listRows.length === 0) {
        setRows([]);
        return;
      }

      const detailsResponses = await Promise.allSettled(
        listRows.map((row) => loadTemplateDetails(selectedJurisdictionId, row.id, row)),
      );

      const loadedRows = [];
      const failedItems = [];

      detailsResponses.forEach((result, index) => {
        const fallbackRow = listRows[index];
        if (result.status === 'fulfilled') {
          loadedRows.push({
            ...result.value,
            id: fallbackRow.id || result.value.id,
          });
        } else {
          loadedRows.push({
            ...fallbackRow,
            subject: '',
            html: '',
          });
          failedItems.push(`${fallbackRow.name} [${fallbackRow.language}]: ${result.reason?.message || 'Request failed'}`);
        }
      });

      setRows(buildEmailTemplatesTableModel(loadedRows));

      if (failedItems.length) {
        setError(`Some templates failed to load details. ${failedItems.join('; ')}`);
      }
    } catch (requestError) {
      if (requestError?.status === 404) {
        setRows([]);
      } else {
        setRows([]);
        setError(requestError.message || 'Failed to load templates');
      }
    } finally {
      setIsLoading(false);
    }
  }, [isJurisdictionsLoading, jurisdictions, loadTemplateDetails, selectedJurisdictionId, token]);

  useEffect(() => {
    loadJurisdictions();
  }, [loadJurisdictions]);

  useEffect(() => {
    let isCancelled = false;

    const loadPlaceholders = async () => {
      try {
        const response = await adminApi.getPlaceholdersDictionary(token);
        if (!isCancelled) {
          setPlaceholderOptions(mapPlaceholdersResponseToDto(response));
        }
      } catch {
        if (!isCancelled) {
          setPlaceholderOptions([]);
        }
      }
    };

    loadPlaceholders();

    return () => {
      isCancelled = true;
    };
  }, [token]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const openCreate = () => {
    setDetailsTargetId('');
    setEditingTargetId('');
    setIsCreating(true);
    const firstUnusedName = EMAIL_TEMPLATE_NAMES.find((name) => (
      rows.every((row) => !(row.name === name && row.language === queryLanguage))
    )) || EMAIL_TEMPLATE_NAMES[0];
    setForm({
      name: firstUnusedName,
      language: queryLanguage,
      subject: '',
      html: '',
    });
    setFormErrors({});
    setError('');
  };

  const openDetails = (id) => {
    setEditingTargetId('');
    setIsCreating(false);
    setDetailsTargetId(String(id || '').trim());
  };

  const openEdit = (row) => {
    setDetailsTargetId('');
    setEditingTargetId(String(row?.id || '').trim());
    setIsCreating(false);
    setForm({
      name: row?.name || EMAIL_TEMPLATE_NAMES[0],
      language: toLowercaseLanguage(row?.language, DEFAULT_LANGUAGE),
      subject: row?.subject || '',
      html: row?.html || '',
    });
    setFormErrors({});
    setError('');
  };

  const closeModals = () => {
    if (isSaving) {
      return;
    }
    setDetailsTargetId('');
    setEditingTargetId('');
    setIsCreating(false);
    setFormErrors({});
  };

  const upsertRow = useCallback((row) => {
    const nextRow = buildEmailTemplatesTableModel([row])[0];
    if (!nextRow) {
      return;
    }

    setRows((prev) => {
      const index = prev.findIndex((item) => item.id === nextRow.id);
      if (index === -1) {
        return buildEmailTemplatesTableModel([...prev, nextRow]);
      }

      const next = [...prev];
      next[index] = nextRow;
      return buildEmailTemplatesTableModel(next);
    });
  }, []);

  const saveEdit = async (event) => {
    event.preventDefault();

    const validationErrors = validateEmailTemplateForm(form);
    if (Object.keys(validationErrors).length) {
      setFormErrors(validationErrors);
      setError('Please fix validation errors.');
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');

    const body = {
      name: String(form.name || '').trim(),
      language: toLowercaseLanguage(form.language),
      subject: String(form.subject || '').trim(),
      html: normalizeTemplateHtmlForSave(String(form.html || '').trim()),
    };

    try {
      if (!selectedJurisdictionId) {
        throw new Error('Jurisdiction is required');
      }

      const response = await adminApi.requestAdmin({
        token,
        method: isCreating ? 'POST' : 'PATCH',
        path: isCreating
          ? `/admin/system/user-settings/jurisdictions/${encodeURIComponent(selectedJurisdictionId)}/email-templates`
          : `/admin/system/user-settings/jurisdictions/${encodeURIComponent(selectedJurisdictionId)}/email-templates/${encodeURIComponent(editingTargetId)}`,
        body,
      });

      const updatedDto = mapEmailTemplateResponseToDto(response, body.name, body.language);
      upsertRow({
        ...updatedDto,
        id: String(updatedDto.id || editingTargetId),
      });

      setEditingTargetId('');
      setIsCreating(false);
      setFormErrors({});
      setSuccess(isCreating ? 'Template created.' : 'Template updated.');
    } catch (requestError) {
      const nextFieldErrors = {};
      const details = Array.isArray(requestError?.errors) ? requestError.errors : [];

      details.forEach((item) => {
        const parameter = String(item?.parameter || '').trim();
        if (parameter === 'name' || parameter === 'language' || parameter === 'subject' || parameter === 'html') {
          nextFieldErrors[parameter] = String(item?.message || 'Invalid value').trim() || 'Invalid value';
        }
      });

      setFormErrors(nextFieldErrors);
      setError(requestError.message || 'Failed to save template');
    } finally {
      setIsSaving(false);
    }
  };

  return {
    rows,
    jurisdictions,
    selectedJurisdictionId,
    isJurisdictionsLoading,
    isLoading,
    isSaving,
    error,
    success,
    languageOptions,
    placeholderOptions,
    wrapLink,
    detailsRow,
    editingTargetId,
    isCreating,
    form,
    formErrors,
    setForm,
    setFormErrors,
    setSelectedJurisdictionId,
    updateJurisdictionQuery,
    openCreate,
    openDetails,
    openEdit,
    closeModals,
    saveEdit,
  };
}
